import * as React from "react";
import { connect } from "react-redux";
import { ApplicationState } from "../../store";
import * as ClientUsersState from "../../store/ClientUsers";
import { PageControl } from "./PageControl";
import { debounce } from "lodash";
import { defaultPageSize } from "../../helpers";
import { ConnectUserResponse } from "../../api/ApiClient";
import { Spinner } from "./Spinner";
import SearchInput from "./SearchInput";
import { useSnackbar } from "notistack";
import { Button } from "components/ui/button";
import { AdminTable } from "./AdminTable";

interface Injected {
  clientUsers: ClientUsersState.ClientUsersState;
}

type OwnProps = {
  onSelected: (site: ConnectUserResponse) => any;
  pageSize?: number;
  selectedClientUsers?: Array<ConnectUserResponse>;
  renderButton?: (site: ConnectUserResponse) => any;
  selectedLabel?: string;
  selectedBtnClass?: string;
  unselectedLabel?: string;
  unselectedBtnClass?: string;
  btnStyle?: Object;
};

interface SnackProps {
  enqueueSnackbar?: (node: React.ReactNode, options?: any) => void;
  closeSnackbar?: (key?: any) => void;
}

type Props = Injected &
  typeof ClientUsersState.actionCreators &
  OwnProps &
  SnackProps;

interface State {
  typedClientUser: string;
}

class SelectClientUser extends React.Component<Props, State> {
  state: State = {
    typedClientUser: "",
  };

  componentDidMount() {
    console.log("SelectClientUser: Requesting clientUsers...");
    this.refresh();
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.pageSize !== prevProps.pageSize) {
      this.refresh();
    }
  }

  refresh = async () => {
    try {
      const { pageSize } = this.props;
      const { typedClientUser: searchTerm } = this.state;
      await this.props.setFetchParamClientUsers({
        page: 0,
        pageSize: pageSize || defaultPageSize,
        searchTerm,
      });
    } catch (e) {
      console.error("Error requesting clientUsers", e);
      const message = (e as any)?.message
        ? (e as any).message
        : "Error requesting clientUsers";
      this.props.enqueueSnackbar &&
        this.props.enqueueSnackbar(
          <span style={{ fontSize: 12 }}>{message}</span>,
          { variant: "error" },
        );
    }
  };

  handleTypedClientUser = (value: string) => {
    this.setState({ typedClientUser: value }, this.searchClientUser);
  };

  searchClientUser = debounce(this.refresh, 1000);

  public render() {
    const { clientUsers, searchTerm, isLoading } = this.props.clientUsers;

    const { typedClientUser } = this.state;

    return (
      <div>
        <SearchInput
          id="typedClientUser"
          name="typedClientUser"
          placeholder="type here"
          value={typedClientUser}
          onChange={(e) => this.handleTypedClientUser(e.target.value)}
        />
        <h4>
          {searchTerm ? `Users Matching '${searchTerm}'` : "Users"}
          {isLoading && <Spinner />}
        </h4>
        {this.renderClientUsersTable(clientUsers)}
      </div>
    );
  }

  private defaultRenderButton = (clientUser: ConnectUserResponse) => {
    const {
      selectedClientUsers,
      onSelected,
      btnStyle,
      selectedBtnClass,
      selectedLabel,
      unselectedBtnClass,
      unselectedLabel,
    } = this.props;
    const isSelected =
      selectedClientUsers &&
      selectedClientUsers.find((a) => a.id === clientUser.id);
    const className = isSelected
      ? selectedBtnClass || "btn-primary"
      : unselectedBtnClass || "btn-default";
    return (
      <Button
        className={className}
        style={btnStyle as React.CSSProperties | undefined}
        type="button"
        variant={isSelected ? "default" : "outline"}
        onClick={() => onSelected(clientUser)}
      >
        {!isSelected
          ? unselectedLabel || "Select"
          : selectedLabel || "Unselect"}
      </Button>
    );
  };

  private renderClientUsersTable(clientUsers: ConnectUserResponse[]) {
    const { renderButton } = this.props;
    const { isLoading } = this.props.clientUsers;
    return (
      <div>
        <AdminTable aria-label="Client users">
          <thead>
            <tr>
              <th className="w-[100px]" />
              <th>Id</th>
              <th>Email</th>
              <th>Name</th>
            </tr>
          </thead>
          <tbody>
            {clientUsers.map((clientUser, i) => (
              <tr key={"f_" + i}>
                <td className="w-[100px]">
                  {renderButton
                    ? renderButton(clientUser)
                    : this.defaultRenderButton(clientUser)}
                </td>
                <td>{clientUser.id}</td>
                <td>{clientUser.email}</td>
                <td>{clientUser.name}</td>
              </tr>
            ))}
          </tbody>
        </AdminTable>

        <PageControl
          onNext={this.props.nextClientUserPage}
          onPrev={this.props.prevClientUserPage}
          currentPage={this.props.clientUsers.page}
          isLoading={isLoading}
          disablePrev={this.props.clientUsers.page === 0}
          disableNext={
            this.props.clientUsers.clientUsers.length <
            this.props.clientUsers.pageSize
          }
        />
      </div>
    );
  }
}

const ConnectedSelectClientUser = connect<any, any, OwnProps, ApplicationState>(
  (state) => ({
    clientUsers: state.clientUsers,
  }),
  { ...ClientUsersState.actionCreators },
)(SelectClientUser);

const SelectClientUserWithSnackbar: React.FC<any> = (props) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  return (
    <ConnectedSelectClientUser
      {...props}
      enqueueSnackbar={enqueueSnackbar}
      closeSnackbar={closeSnackbar}
    />
  );
};

export default SelectClientUserWithSnackbar;
